import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import PageMeta from "@/components/PageMeta";
import { api } from "@/lib/api";

export default function LawyerDetail() {
  const { slug } = useParams();
  const [lawyer, setLawyer] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/lawyers/${slug}`).then((r) => setLawyer(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <SiteLayout>
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-32">
          <h1 className="h-display text-4xl">Person not found.</h1>
          <Link to="/people" className="link-grow mt-6 inline-flex">Back to People</Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      {lawyer && (
        <PageMeta
          title={`${lawyer.name} | ${lawyer.department?.title || "Carlton Elleker Chase"}`}
          description={lawyer.bio_excerpt || `${lawyer.name}${lawyer.title ? ", " + lawyer.title : ""}, Carlton Elleker Chase.`}
          image={lawyer.photo_url}
          type="profile"
        />
      )}
      <article className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 pt-10 md:pt-20" data-testid="lawyer-detail">
        <Link to="/people" className="inline-flex items-center gap-2 overline text-graphite/60 hover:text-[#1E3668]" data-testid="lawyer-back">
          <ArrowLeft size={14} /> All people
        </Link>

        {lawyer && (
          <div className="mt-10 md:mt-16 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-5">
              <div className="aspect-[4/5] overflow-hidden bg-[#EEEAE3]">
                {lawyer.photo_url && (
                  <img src={lawyer.photo_url} alt={lawyer.name} className="w-full h-full object-cover bw-portrait" />
                )}
              </div>
            </div>
            <div className="md:col-span-7">
              <p className="text-sm text-graphite/55">{lawyer.department?.title || lawyer.title}</p>
              <h1 className="mt-4 h-display text-[clamp(2.4rem,5.5vw,5rem)]" data-testid="lawyer-name">{lawyer.name}</h1>

              <div className="mt-10 hairline-top hairline-bottom py-5 flex flex-wrap gap-x-10 gap-y-2 text-sm">
                {lawyer.department && (
                  <div>
                    <p className="text-xs text-graphite/55 mb-1">Department</p>
                    <Link to={`/expertise/${lawyer.department.slug}`} className="inline-block hover:text-[#1E3668]">{lawyer.department.title}</Link>
                  </div>
                )}
                <div>
                  <p className="text-xs text-graphite/55 mb-1">Location</p>
                  <p>{lawyer.location}</p>
                </div>
                {lawyer.email && (
                  <div>
                    <p className="text-xs text-graphite/55 mb-1">Email</p>
                    <a className="inline-block hover:text-[#1E3668]" href={`mailto:${lawyer.email}`}>{lawyer.email}</a>
                  </div>
                )}
                {lawyer.phone && (
                  <div>
                    <p className="text-xs text-graphite/55 mb-1">Direct</p>
                    <p>{lawyer.phone}</p>
                  </div>
                )}
              </div>

              <div
                className="mt-10 max-w-2xl text-graphite/85 leading-relaxed prose prose-cec [&_p]:mb-4"
                data-testid="lawyer-bio"
                dangerouslySetInnerHTML={{ __html: lawyer.bio || "" }}
              />

              {lawyer.services?.length > 0 && (
                <div className="mt-14" data-testid="lawyer-services">
                  <h2 className="h-display text-2xl md:text-3xl">{lawyer.name.split(" ")[0]} works on</h2>
                  <ul className="mt-8 hairline-top">
                    {lawyer.services.map((s) => (
                      <li key={s.slug} className="hairline-bottom num-list-row">
                        <Link to={`/services/${s.slug}`} className="flex items-center justify-between py-4">
                          <span className="num-title font-medium pr-4">{s.title}</span>
                          <ArrowUpRight size={18} className="text-graphite/40 num-arrow" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </article>
    </SiteLayout>
  );
}
